import React from "react";

import { cn } from "../../../lib/utils";
import { Input } from "../../../components/ui/input";
import { Switch } from "../../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

export const inputClass = "bg-white/5 text-white placeholder:text-white/40";

interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, error, hint, children, className }: FieldProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.3em] text-white/50">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-red-400">{error}</p>
      ) : hint ? (
        <p className="text-xs text-white/40">{hint}</p>
      ) : null}
    </div>
  );
}

interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label: string;
  error?: string;
  hint?: string;
  onChange: (value: string) => void;
}

export function TextField({ label, error, hint, onChange, id, className, ...props }: TextFieldProps) {
  return (
    <Field label={label} htmlFor={id} error={error} hint={hint}>
      <Input
        id={id}
        className={cn(inputClass, className)}
        onChange={(e) => onChange(e.target.value)}
        {...props}
      />
    </Field>
  );
}

interface NumberFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  label: string;
  value: number | "";
  error?: string;
  hint?: string;
  onChange: (value: number | "") => void;
}

export function NumberField({ label, error, hint, value, onChange, id, className, ...props }: NumberFieldProps) {
  return (
    <Field label={label} htmlFor={id} error={error} hint={hint}>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        className={cn(inputClass, className)}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        {...props}
      />
    </Field>
  );
}

interface SwitchFieldProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function SwitchField({ label, description, checked, onChange }: SwitchFieldProps) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
      <span>
        <span className="block text-sm font-semibold text-white">{label}</span>
        {description && <span className="block text-xs text-white/50">{description}</span>}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  groups?: SelectOptionGroup[];
  placeholder?: string;
  error?: string;
  hint?: string;
}

const renderOption = (option: SelectOption) => (
  <SelectItem key={option.value} value={option.value}>
    {option.label}
    {option.description && (
      <span className="ml-1.5 text-xs text-muted-foreground">{option.description}</span>
    )}
  </SelectItem>
);

export function SelectField({
  label,
  value,
  onChange,
  options = [],
  groups = [],
  placeholder,
  error,
  hint,
}: SelectFieldProps) {
  return (
    <Field label={label} error={error} hint={hint}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={inputClass}>
          <SelectValue placeholder={placeholder || "Selecione"} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map(renderOption)}
          {groups
            .filter((group) => group.options.length > 0)
            .map((group) => (
              <SelectGroup key={group.label}>
                <SelectLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </SelectLabel>
                {group.options.map(renderOption)}
              </SelectGroup>
            ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">{title}</p>
      {children}
    </section>
  );
}

export function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
        active ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-emerald-400" : "bg-white/40")} />
      {active ? "Ativa" : "Inativa"}
    </span>
  );
}
